import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { features } from "../../../mocks/bizlab";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import FeatureDetailModal from "./FeatureDetailModal";

export default function KeyFeaturesSection() {
  const { ref, isVisible } = useScrollAnimation();
  const [selectedFeature, setSelectedFeature] = useState<(typeof features)[0] | null>(null);

  return (
    <section id="features" className="py-24 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3 block">
            Core Capabilities
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Everything Your Team Needs
          </h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto leading-relaxed">
            Integrated solutions for modern workplace management — from approvals to assets,
            all in one powerful platform.
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              layoutId={`feature-card-${feature.id}`}
              onClick={() => setSelectedFeature(feature)}
              className={`group bg-white border border-slate-100 rounded-2xl p-7 hover:border-blue-100 hover:shadow-sm transition-colors duration-300 cursor-pointer ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{
                transitionDelay: `${index * 80}ms`,
                willChange: "transform",
              }}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
            >
              {/* Icon */}
              <motion.div
                layoutId={`feature-icon-${feature.id}`}
                className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-5`}
              >
                <i className={`${feature.icon} text-xl`} />
              </motion.div>

              <motion.h3
                layoutId={`feature-title-${feature.id}`}
                className="text-base font-semibold text-slate-900 mb-2"
              >
                {feature.title}
              </motion.h3>

              <p className="text-sm text-slate-500 leading-relaxed">
                {feature.description}
              </p>

              <div className="mt-5 flex items-center gap-1 text-blue-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span>Learn more</span>
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* AnimatePresence로 모달 관리 */}
      <AnimatePresence>
        {selectedFeature && (
          <FeatureDetailModal
            feature={selectedFeature}
            onClose={() => setSelectedFeature(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
